/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosButton, {ArgosLoadingButton} from "../../../../../components/Buttons/ArgosButton"
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import ArgosMenuItem from "../../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../../components/Form/Select/ArgosSelect";
import {ArgosSelectOption} from "../../../../../components/Form/Select/types";
import {ArgosOpenInNewTabIcon as OpenInNewIcon, ArgosViewDetailsIcon} from "../../../../../components/Icons/ArgosActionIcons";
import {cloneDeep} from 'lodash';
import FormControl from "@mui/material/FormControl";
import {IComponent} from "../../../../../components/Global/Dialog/GlobalDialog";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import {MRT_Cell, MRT_ColumnDef} from "material-react-table";
import {
  generateBlueLineEndpoint,
  getPerformanceTableEndpoint,
  idToLabelMergeConfigurationsMap,
  ModalityPerformanceCell,
  PerformanceRow,
  renderPerformanceCell,
  SensingSysPerfProps,
  updatePerformanceTableEndpoint
} from "./constants"
import React, {useEffect, useMemo, useState} from 'react';
import ReportDialog from "./ReportDialog";
import {requestIdToken} from "../../../../../../auth/authConfig";
import {
  EBluelineReportStatusMapRequestTypes,
  selectDialogState,
  selectVehicleProgramsMainData,
  selectVehicleProgramsSelectedDevelopmentStageData,
  selectVehicleProgramsSensingSystemPerformanceState,
  setDialogComponent,
  setIsReportDialogOpen, setReportGenerationStatusMap,
  updateReportStatusMap
} from "../../../../../../redux/ducks/VehicleProgramsDetails/slice";
import {setGlobalRightClickMenuData, setGlobalRightClickMenuPosition} from "../../../../../../redux/ducks/GlobalRightClickMenu/slice";
import ArgosDialog, {StyledArgosDialog} from "../../../../../components/Dialogs/ArgosDialog";
import {ARGOS_REPORTS_STATE_TO_COLOR, ARGOS_WARNING, EArgosReportsState, tableButtonGray, tableHeaderGray,} from "../../../../../../constants/colors";
import toast from "react-hot-toast"
import Tooltip from "@mui/material/Tooltip";
import {Typography} from "@mui/material";
import {useAppSelector} from "../../../../../../redux/hooks";
import {useDispatch} from "react-redux";
import {useMsal} from "@azure/msal-react";
import {usePostRequest} from "../../../../../../api/helper"
import useGetUrlParams from "../../../../../../hooks/useGetUrlParams";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../../constants";
import {useHasWriteAccess} from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import Loader from "../../../../../components/Loader/Loader";
import BoxWithCenteredContent from "../../../../../components/Containers/BoxWithCenteredContent";

import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";
import {openViewDataSetErrorsDialog} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import {ViewGenerateBluelineErrorDialog} from "./ViewGenerateBluelineErrorDialog";

const SensingSysPerf = (props: SensingSysPerfProps) => {
  const dispatch = useDispatch()
  const isReportDialogOpen = useAppSelector(state => selectDialogState(state).isReportDialogOpen)
  const {developmentStageId, vehicleProgramId, adasProgramId} = useGetUrlParams()
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true)

  /**
   * Report generation status
   */
  const reportGenerationStatusObject = useAppSelector(state => selectVehicleProgramsSensingSystemPerformanceState(state).reportGenerationStatusObjectMap?.[developmentStageId] ?? {})
  const reportGenerationStatus = reportGenerationStatusObject?.["status"]
  const isLineGenerationLoading = reportGenerationStatus === "In Progress"

  const [performanceTableRows, setPerformanceTableRows] = useState<PerformanceRow[]>([])
  const refPerformanceTableRows = React.useRef(performanceTableRows)
  useEffect(() => {
    refPerformanceTableRows.current = performanceTableRows
  }, [performanceTableRows]);
  const developmentStageData = useAppSelector(selectVehicleProgramsSelectedDevelopmentStageData)
  const [mergeConfigurationOptions, setMergeConfigurationOptions] = useState<ArgosSelectOption[]>([{id: "all", label: "All"}])
  const [selectedMergeConfiguration, setSelectedMergeConfiguration] = useState<string>("all")

  const {instance, accounts} = useMsal()

  /**
   * This is what is used in the table. It is filtered based on the selected merge configuration.
   */
  const filteredPerformanceTableRows = useMemo(() => {
    const _tableData = performanceTableRows
    if (_tableData != null) {
      let _filteredTableData = _tableData
      if (selectedMergeConfiguration !== 'all') {
        _filteredTableData = _filteredTableData.filter((row: any) => {
          const mergeConfiguration = row?.mergeConfiguration
          return mergeConfiguration === selectedMergeConfiguration
        })
      }
      return _filteredTableData
    } else {
      return performanceTableRows
    }
  }, [performanceTableRows, selectedMergeConfiguration]);

  useEffect(() => {
    getMergeConfigurationOptions();
  }, [performanceTableRows])

  const getMergeConfigurationOptions = () => {
    let _localMergeConfigurationOptions: any[] = [];
    let summaryTableData: any = performanceTableRows
    if (summaryTableData != null) {
      summaryTableData?.forEach((row: any) => {
        const _mergeConfiguration = row.mergeConfiguration
        const _mergeConfigurationOption = {
          "id": _mergeConfiguration,
          "label": idToLabelMergeConfigurationsMap?.[_mergeConfiguration] ?? _mergeConfiguration
        }
        if (!_localMergeConfigurationOptions.some((option: any) => option.id === _mergeConfigurationOption.id)) {
          _localMergeConfigurationOptions.push({
            "id": _mergeConfiguration,
            "label": idToLabelMergeConfigurationsMap?.[_mergeConfiguration] ?? _mergeConfiguration
          })
        }
      })
    }
    setMergeConfigurationOptions(_localMergeConfigurationOptions)
  }
  useEffect(() => {
    if (adasProgramId) {
      handleGetPerformanceTable(true)
    }
  }, [selectedMergeConfiguration, adasProgramId])

  useEffect(() => {
    if (reportGenerationStatus === "Completed") {
      setSelectedMergeConfiguration("all")
      handleGetPerformanceTable(true)
    }
  }, [reportGenerationStatus])

  const handleSaveCell = (cell: MRT_Cell<PerformanceRow>, value: any) => {
    const _rowsData = cloneDeep(performanceTableRows)
    _rowsData[cell.row.index][cell.column.id as keyof PerformanceRow] = value
    setPerformanceTableRows(_rowsData)
    handleUpdatePerformanceTable({
      "vehicleProgramId": vehicleProgramId,
      "developmentStageId": developmentStageId,
      "rowsData": cloneDeep(_rowsData)
    })
  }

  const updatePerformanceTableHandle = usePostRequest(updatePerformanceTableEndpoint)
  const handleUpdatePerformanceTable = (data: any) => {
    setIsTableLoading(true)
    updatePerformanceTableHandle(data).then((res: any) => {
      setPerformanceTableRows(res)
    }).catch((err: any) => {
      console.log(err)
      toast.error("An error occurred while saving your changes")
    }).finally(() => {
      setIsTableLoading(false)
    })
  }

  const redlineIdToReportsStatusMap = useAppSelector(state => selectVehicleProgramsSensingSystemPerformanceState(state).devStageToRedlineIdToReportStatus?.[developmentStageId]?.[adasProgramId] ?? {})
  const performanceTableColumns = useMemo<MRT_ColumnDef<PerformanceRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Report Name",
        enableEditing: false,
      },
      {
        accessorKey: 'datasetState',
        header: 'Status After Generate',
        id: 'datasetState',
        enableEditing: false,
        size: 200,
        Cell: ({cell, row}) => {
          const rowData = row.original
          // const redlineDatasetId = rowData.redlineId
          const adasReportId = rowData.adasReportId
          // const datasetState = cell.getValue<string>()
          const datasetState = redlineIdToReportsStatusMap?.[adasReportId]?.state
          const backgroundColor = ARGOS_REPORTS_STATE_TO_COLOR?.[datasetState] ?? ''
          let label: any = datasetState
          if (datasetState === EArgosReportsState.PENDING)
            label = `Pending dependent merged line being regenerated`
          else if (datasetState === EArgosReportsState.MODIFIED || datasetState === EArgosReportsState.DELETED)
            label = `${datasetState}: Press Generate Performance Reports`
          if (datasetState === EArgosReportsState.DELETED)
            label += ' to delete report'
          else if (datasetState === EArgosReportsState.MODIFIED)
            label += ' to update report'
          return (
            <span style={{color: backgroundColor, whiteSpace: 'normal'}}>{label}</span>
          )
        }
      },
      {
        id: 'stateReason',
        accessorKey: "stateReason",
        header: "Status Reason",
        enableEditing: false,
        Cell: ({cell, row}) => {
          const rowData = row.original
          const adasReportId = rowData.adasReportId
          const reason = redlineIdToReportsStatusMap?.[adasReportId]?.reason

          return (
            <span style={{whiteSpace: 'normal'}}>{reason}</span>
          )
        }
      },
      {
        accessorKey: "mergeConfiguration",
        header: "Merge Configuration",
        minSize: 80,
        size: 220,
        enableEditing: false,
        Cell: ({cell}) => idToLabelMergeConfigurationsMap?.[cell.getValue() as string] ?? cell.getValue(),
      },
      {
        accessorKey: "camera",
        header: "Camera",
        enableEditing: false,
        Cell: ({cell}) => renderPerformanceCell(cell.getValue() as ModalityPerformanceCell),
      },
      {
        accessorKey: "radar",
        header: "Radar",
        enableEditing: false,
        Cell: ({cell}) => renderPerformanceCell(cell.getValue() as ModalityPerformanceCell),
      },
      {
        accessorKey: "comments",
        header: "Comments",
        enableEditing: true,
        muiEditTextFieldProps: ({cell}) => ({
          onKeyDown: (e) => {
            if (e.code == "Tab") {
              e.stopPropagation()
              e.preventDefault()
            }
          },
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value)
          }
        })
      }
    ], [performanceTableRows, redlineIdToReportsStatusMap]
  )

  const tableToolbarActionsLeft = (table: any) => {
    return (
      <ArgosFlexbox alignItems={'center'} style={{}}>
        <FormControl variant="standard">
          <InputLabel id="merge-configuration-label">Merge Configuration</InputLabel>
          <ArgosSelect
            required
            labelId="merge-configuration-label"
            label="Merge Configuration"
            type="text"
            value={selectedMergeConfiguration}
            id="merge-configuration"
            onChange={(e) => handleMergeConfigurationChange(e)}
          >
            {finalMergeConfigurationOptions.map((option: ArgosSelectOption) =>
              <ArgosMenuItem key={option.id} value={option.id}>
                {option.label}
              </ArgosMenuItem>
            )}
          </ArgosSelect>
        </FormControl>
      </ArgosFlexbox>
    )
  }

  const tableRowActions = (row: any, dispatch: any) => {
    const adasReportId: string = row.original.adasReportId
    const datasetState: string = row.original?.datasetState
    const disableViewIcon = datasetState === EArgosReportsState.MODIFIED || datasetState === EArgosReportsState.DELETED

    const url = `${VEHICLE_PROGRAMS_CONSTANTS.itemDetails.sensingSystemPerformance.viewReportRoute}?` +
      `vehicleProgramId=${vehicleProgramId}` +
      `&developmentStageId=${developmentStageId}` +
      `&adasReportId=${adasReportId}` +
      `&adasProgramId=${adasProgramId}`;

    return (
      <ArgosFlexbox
        noGap
        style={{
          display: 'flex',
          flexWrap: 'nowrap'
        }}
      >
        {!disableViewIcon && <Tooltip title={"View Report"} arrow>
          <a
            onClick={(e) => {
              if (disableViewIcon) return
              e.preventDefault()
              const component: IComponent = {
                'params': {adasReportId},
                'name': 'VehicleProgramItemDetails'
              }
              dispatch(setDialogComponent(component))
              dispatch(setIsReportDialogOpen(true))
            }}
            onContextMenu={(e) => {
              if (disableViewIcon) return
              e.preventDefault()
              dispatch(setGlobalRightClickMenuData({url}))
              dispatch(setGlobalRightClickMenuPosition({x: e.pageX, y: e.pageY}))
            }}
            // href={url}
            // target={'_blank'}
          >
            <IconButton size="large" disabled={disableViewIcon}>
              <ArgosViewDetailsIcon fontSize="medium" style={{color: tableButtonGray}}/>
            </IconButton>
          </a>
        </Tooltip>}
      </ArgosFlexbox>
    )
  }

  const handleMergeConfigurationChange = (event: any) => {
    setSelectedMergeConfiguration(event.target.value)
  }

  const handleGetInfoToDisplayBeforeGeneratingBlueline = usePostRequest(VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.getInfoToDisplayBeforeGeneratingBlueline)
  const [isGetUpdateBluelineSummaryLoading, setIsGetUpdateBluelineSummaryLoading] = useState<boolean>(false)
  const [isGenerateBluelinesDialogOpen, setIsGenerateBluelinesDialogOpen] = useState<boolean>(false)
  const [updateBluelineSummary, setUpdateBluelineSummary] = useState<any>(null)
  const [infoBeforeGenerateBluelines, setInfoBeforeGenerateBluelines] = useState<any>(null)


  const validAdasReports = useMemo(() => {
    if(!infoBeforeGenerateBluelines || !infoBeforeGenerateBluelines.adasReportIds || !infoBeforeGenerateBluelines.invalidReports) {
      return []
    }
    const _adasReportIds: string[] = infoBeforeGenerateBluelines?.adasReportIds ?? [];
    const _invalidReportIds = Object.keys(infoBeforeGenerateBluelines?.invalidReports ?? {});
    return _adasReportIds.filter((id) => !_invalidReportIds.includes(id));
  }, [infoBeforeGenerateBluelines])

  const handleClickGenerateBlueLine = () => {
    setIsGenerateBluelinesDialogOpen(true)
    setIsGetUpdateBluelineSummaryLoading(true)
    handleGetInfoToDisplayBeforeGeneratingBlueline({
      "vehicleProgramId": vehicleProgramId,
      "developmentStageId": developmentStageId,
      adasProgramId,
    }).then((res: any) => {
      setInfoBeforeGenerateBluelines(res)
      setUpdateBluelineSummary(res?.mergeConfigurationMap ?? {})
    }).catch((err: any) => {
      console.log(err)
      toast.error("Error getting info to display before generating blue line")
      setUpdateBluelineSummary({})
      setInfoBeforeGenerateBluelines(null)
    }).finally(() => {
      setIsGetUpdateBluelineSummaryLoading(false)
    })
  }

  const invalidObjects = infoBeforeGenerateBluelines?.invalidObjects ?? null
  const objectIdToNameMap = infoBeforeGenerateBluelines?.objectIdToNameMap ?? null
  const invalidSensors = infoBeforeGenerateBluelines?.invalidSensors ?? null
  const anyValidSensors = infoBeforeGenerateBluelines?.anyValidSensors ?? false

  const adasReportIds = infoBeforeGenerateBluelines?.adasReportIds ?? null

  const invalidReports = infoBeforeGenerateBluelines?.invalidReports ?? null

  const validRedlineModalities = infoBeforeGenerateBluelines?.validRedlineModalities ?? null

  const reportIdToName = infoBeforeGenerateBluelines?.reportIdToName ?? null

  const invalidKeys = [
    'invalidRedline',
    'invalidRedlineObjects',
    'invalidAdasReportObjects',
    'invalidRedlineModalities',
    'noModalitiesWithValidSensors',
  ];

  const isReportInvalid = (report: any) =>
    invalidKeys.some(key => report?.[key]);

  const allReportsInvalid = adasReportIds
    ? adasReportIds.every(
      (id: string) => isReportInvalid(infoBeforeGenerateBluelines?.invalidReports?.[id])
    )
    : true;

  const isGenerateBluelinesDisabled = allReportsInvalid;

  const handleGenerateBlueLine = async () => {
    const reqBody = {
      "vehicleProgramId": vehicleProgramId,
      "developmentStageId": developmentStageId,
      adasProgramId,
      validAdasReports
    }
    const idTok = await requestIdToken(instance, accounts)
    fetch(generateBlueLineEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${idTok}`
      },
      body: JSON.stringify(reqBody)
    }).then((res: any) => {
      setSelectedMergeConfiguration("all")
    }).catch((err: any) => {
      console.log(err)
      toast.error("Error generating blue line")
    })
  }

  const getPerformanceTableHandle = usePostRequest(getPerformanceTableEndpoint)
  const handleGetPerformanceTable = (checkForOutdatedDatasets: boolean) => {
    const reqBody = {
      vehicleProgramId,
      developmentStageId,
      adasProgramId,
      "view": selectedMergeConfiguration,
      checkForOutdatedDatasets
    }
    setIsTableLoading(true)
    getPerformanceTableHandle(reqBody).then((res: any) => {
      setIsTableLoading(false)
      /**
       * We have redux map storing the report statuses for each redline dataset id. When we load here, we need to update that map, but only add new ids, don't overwrite existing ones.
       */
      const tableData = res?.tableData
      if (tableData == null) {
        return
      }
      // loop through the rows
      const redlineDatasetIdsToStatusMap: any = {}
      tableData.forEach((row: any) => {
        // const redlineDatasetId = row.redlineId
        const adasReportId = row.adasReportId
        redlineDatasetIdsToStatusMap[adasReportId] = {state: row.datasetState, reason: row.stateReason}
      })
      const dispatchPayload = {
        "data": {
          "developmentStageId": developmentStageId,
          "adasProgramId": adasProgramId,
          "redlineDatasetIdToStatusMap": redlineDatasetIdsToStatusMap
        },
        // "requestType": EBluelineReportStatusMapRequestTypes.ONLY_ADD_NEW  // only add new, because socket is faster, so this could overwrite what socket set.
        "requestType": EBluelineReportStatusMapRequestTypes.UPDATE
      }
      dispatch(updateReportStatusMap(dispatchPayload))
      setPerformanceTableRows(tableData)
    }).catch((err: any) => {
      console.log(err)
      toast.error("Error getting performance table data")
      setIsTableLoading(false)
    })
  }
  const isProgramArchive = useAppSelector(state => selectVehicleProgramsMainData(state)?.isProgramArchive)
  const writeAccess = useHasWriteAccess()
  const hasWriteAccess = !isProgramArchive && writeAccess
  const finalMergeConfigurationOptions = useMemo(() => {
    // add All option always
    return [{id: "all", label: "All"}, ...mergeConfigurationOptions]
  }, [mergeConfigurationOptions])

  const shouldShowGeneratePerformanceReportsButton = true

  const handleFetchReportGenerationStatusObject = usePostRequest(VEHICLE_PROGRAMS_CONSTANTS.itemDetails.sensingSystemPerformance.fetchReportGenerationStatusObject)
  const [isLoadingReportGenerationStatusObject, setIsLoadingReportGenerationStatusObject] = useState<boolean>(true)
  useEffect(() => {
    if (vehicleProgramId == null || developmentStageId == null || adasProgramId == null) {
      return
    }
    const payload = {
      vehicleProgramId,
      developmentStageId,
      adasProgramId
    }
    setIsLoadingReportGenerationStatusObject(true)
    handleFetchReportGenerationStatusObject(payload).then((res: any) => {
      const reportGenerationStatusObjectMap = {
        [developmentStageId]: res
      }
      const dispatchData = {
        reportGenerationStatusObjectMap,
      }
      const dispatchPayload = {
        data: dispatchData,
        requestType: 'update'
      }
      dispatch(setReportGenerationStatusMap(dispatchPayload))

    }).catch((err: any) => {
      console.log(err)
      toast.error("Error fetching report generation status object")
    }).finally(() => {
      setIsLoadingReportGenerationStatusObject(false)
    })
    }, [vehicleProgramId, developmentStageId, adasProgramId]);

  const [isViewDataSetErrorsDialogOpen, setIsViewDataSetErrorsDialogOpen] = useState<boolean>(false)

  const generateBluelineDetailsDialogMaxHeight = 70
  const isContentLoading = isTableLoading
  return (
    <>
      <ViewGenerateBluelineErrorDialog isOpen={isViewDataSetErrorsDialogOpen} setIsOpen={setIsViewDataSetErrorsDialogOpen} data={reportGenerationStatusObject?.error} />
      {!shouldShowGeneratePerformanceReportsButton ?
        <Loader isLoading={isContentLoading && shouldShowGeneratePerformanceReportsButton == null}>
          {shouldShowGeneratePerformanceReportsButton != null &&
            <BoxWithCenteredContent vertical horizontal> Add reports in ADAS Programs </BoxWithCenteredContent>
          }
        </Loader>
        :
        <ArgosFlexbox vertical>
          <ArgosFlexbox alignItems={'center'}>
            {(hasWriteAccess && shouldShowGeneratePerformanceReportsButton) &&
              <ArgosLoadingButton isLoading={isLineGenerationLoading} onClick={handleClickGenerateBlueLine}
                                  disabled={isLineGenerationLoading}>
                Generate Performance Reports
              </ArgosLoadingButton>}
            {Object.keys(reportGenerationStatusObject).length !== 0 && <Typography>
              {reportGenerationStatusObject?.["status"]}
            </Typography>}

            {Object.keys(reportGenerationStatusObject).length !== 0 && reportGenerationStatusObject?.status === 'Error' && <Tooltip title='View Error Details'>
              <IconButton onClick={() => setIsViewDataSetErrorsDialogOpen(true)}>
                <OpenInNewIcon style={{color: tableButtonGray}}/>
              </IconButton>
            </Tooltip>
            }
            {Object.keys(reportGenerationStatusObject).length !== 0 && reportGenerationStatusObject?.["stageCount"] !== null && reportGenerationStatusObject?.["stageName"] !== null &&
              <Typography>
                {reportGenerationStatusObject?.["stageName"]}
              </Typography>}
            {Object.keys(reportGenerationStatusObject).length !== 0 && reportGenerationStatusObject?.["percentage"] !== null && <Typography>
              ({reportGenerationStatusObject?.["percentage"]}% complete)
            </Typography>}
          </ArgosFlexbox>
          <ArgosFlexbox vertical style={{display: isLineGenerationLoading ? "none" : "flex"}}>
            <ArgosTableSimple
              columns={performanceTableColumns}
              data={filteredPerformanceTableRows}

              enableEditing
              editDisplayMode="cell"
              // Row actions
              enableRowActions
              renderRowActions={({row}: any) => tableRowActions(row, dispatch)}
              positionActionsColumn="last"
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  size: 100,
                },
              }}
              renderTopToolbarCustomActions={({table}: any) => tableToolbarActionsLeft(table)}
              muiToolbarAlertBannerProps={() => ({
                sx: {display: 'none'}
              })}
              enablePagination={false}
              state={{
                isLoading: isTableLoading
              }}
              muiTableHeadRowProps={{
                // Header row style
                sx: {backgroundColor: tableHeaderGray},
              }}
            />
          </ArgosFlexbox>
        </ArgosFlexbox>
      }

      <ArgosDialog
        open={isGenerateBluelinesDialogOpen}
        headerTitle={`Generate Performance Reports`}
        headerTitleAlign={'left'}
        width={'auto'} height={'auto'} maxHeight={`${generateBluelineDetailsDialogMaxHeight}vh`}
      >
        <>
          <ArgosFlexbox vertical style={{height: '100%', overflow: 'auto'}}>
            <Loader isLoading={isGetUpdateBluelineSummaryLoading}>
              {updateBluelineSummary != null &&
                <>
                  <span style={{marginBottom: 10, maxWidth: 300}}>
                    {adasReportIds && adasReportIds.length > 0 ? (
                      <span>
                        Are you sure you want to generate performance reports? {Object.keys(updateBluelineSummary).length > 0 ? 'The reports below be modified or deleted:' : ''}
                      </span>
                    ) : (
                      <span>No reports to generate.</span>
                    )}
                  </span>
                  <div style={{overflow: 'auto', maxHeight: `${generateBluelineDetailsDialogMaxHeight - 30}vh`, paddingRight: 30}}>
                    <BulletListRemoveLevels data={updateBluelineSummary}/>
                  </div>
                  {adasReportIds && adasReportIds.length > 0 && (
                    <div style={{marginTop: 16}}>
                      <b>Reports To Generate</b>
                      {(allReportsInvalid || adasReportIds.some((id: string) => id in invalidReports)) && (
                        <b>
                          {' '}(
                          <span style={{color: allReportsInvalid ? 'red' : 'black'}}>
                            {allReportsInvalid
                              ? 'All reports invalid, therefore generate performance reports disabled'
                              : 'Invalid reports will not be generated'}
                          </span>
                          )
                        </b>
                      )}
                      <ul style={{marginLeft: 24}}>
                        {adasReportIds.map((reportId: string) => {
                            const isInvalid = reportId in invalidReports
                            const reportName = reportIdToName?.[reportId] || reportId
                            const title = isInvalid ? `${reportName} - invalid` : `${reportName} - valid`
                            const reportData = invalidReports?.[reportId] || {}
                            return (
                              <li key={reportId}>
                                <span style={{color: isInvalid ? 'red' : 'initial'}}>{title}</span>
                                {isInvalid &&
                                  <ul>
                                    {reportData.invalidRedline && <li>Redline is invalid.</li>}
                                    {reportData.invalidRedlineModalities && reportData.redlineModalities.length > 0 && (
                                      <li>
                                        Redline has no valid modalities. They are: [{reportData.redlineModalities.map((m: string) => `"${m}"`).join(', ')}].
                                        The only valid modalities are [{validRedlineModalities?.map((m: string) => `"${m}"`).join(', ')}].
                                      </li>
                                    )}
                                    {reportData.noModalitiesWithValidSensors && <li>No valid sensors for any modality in the redline.</li>}
                                    {reportData.invalidRedlineObjects && reportData.redlineObjectIds && (
                                      <li>
                                        All objects in redline are invalid:
                                        <ul>
                                          {reportData.redlineObjectIds.map((objId: string) => (
                                            <li key={objId}>
                                              {(objectIdToNameMap?.[objId] || objId)}:
                                              Invalid: {invalidObjects?.[objId]?.invalid?.join(', ') || 'Unknown reason'}
                                            </li>
                                          ))}
                                        </ul>
                                      </li>
                                    )}
                                    {reportData.invalidAdasReportObjects && reportData.adasReportObjectIds && (
                                      <li>
                                        All objects in ADAS report are invalid:
                                        <ul>
                                          {reportData.adasReportObjectIds.map((objId: string) => (
                                            <li key={objId}>
                                              {(objectIdToNameMap?.[objId] || objId)}:
                                              Invalid: {invalidObjects?.[objId]?.invalid?.join(', ') || 'Unknown reason'}
                                            </li>
                                          ))}
                                        </ul>
                                      </li>
                                    )}
                                  </ul>
                                }
                              </li>
                            )
                          }
                        )}
                      </ul>
                    </div>
                  )}
                </>
              }
            </Loader>
            <ArgosFlexbox style={{justifyContent: 'center', overflow: 'auto'}}>
              <ArgosButton
                onClick={() => {
                  setIsGenerateBluelinesDialogOpen(false)
                  setUpdateBluelineSummary(null)
                }}
              >
                CANCEL
              </ArgosButton>
              <ArgosButton
                onClick={() => {
                  setIsGenerateBluelinesDialogOpen(false)
                  setUpdateBluelineSummary(null)
                  handleGenerateBlueLine()
                }}
                disabled={isGetUpdateBluelineSummaryLoading || isGenerateBluelinesDisabled}
              >
                GENERATE PERFORMANCE REPORTS
              </ArgosButton>
            </ArgosFlexbox>
          </ArgosFlexbox>
        </>
      </ArgosDialog>

      <StyledArgosDialog
        width={"85%"}
        height={"80%"}
        open={isReportDialogOpen}
        onClose={() => {
          dispatch(setIsReportDialogOpen(false))
        }}
      >
        <ReportDialog/>
      </StyledArgosDialog>
    </>
  )
}

interface Props {
  data: any;
}

/**
 * This won't remove empty levels; if you don't want them to show them, remove them from data prop before calling.
 * @param data
 * @constructor
 */
const SimpleBulletList: React.FC<Props> = ({data}) => {
  const isObject = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj);
  const isArray = (arr: any) => arr && Array.isArray(arr);

  const renderList = (data: any) => {
    if (isObject(data)) {
      return (
        <ul>
          {Object.entries(data)
            .filter(([key, value]) => value && (isObject(value) || isArray(value)))
            .map(([key, value]) => (
              <li key={key}>
                {key}
                {renderList(value)}
              </li>
            ))}
        </ul>
      );
    } else if (isArray(data)) {
      return (
        <ul>
          {data.map((item: any) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    } else {
      return null;
    }
  };

  return <div>{renderList(data)}</div>;
};
const BulletListRemoveLevels: React.FC<Props> = ({data}) => {
  const isObject = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj);
  const isArray = (arr: any) => arr && Array.isArray(arr);
  const isEmpty = (data: any) => (isObject(data) && Object.keys(data).length === 0) || (isArray(data) && data.length === 0);

  const removeEmptyLevels = (data: any): any => {
    if (isObject(data)) {
      const keys = Object.keys(data);
      if (keys.length === 1 && isEmpty(data[keys[0]])) {
        return removeEmptyLevels(data[keys[0]]);
      } else {
        return Object.fromEntries(
          keys
            .filter((key) => !isEmpty(data[key]))
            .map((key) => [key, removeEmptyLevels(data[key])])
        );
      }
    } else {
      return data;
    }
  };

  const renderList = (data: any) => {
    if (isObject(data)) {
      return (
        <ul>
          {Object.entries(data).map(([key, value]) => (
            <li key={key}>
              {key}
              {renderList(value)}
            </li>
          ))}
        </ul>
      );
    } else if (isArray(data)) {
      return (
        <ul>
          {data.map((item: any) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    } else {
      return null;
    }
  };

  return <div>{renderList(removeEmptyLevels(data))}</div>;
};

export default SensingSysPerf
